import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Box,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  useSidePanel,
} from '@node-space/ui-components'
import { ChannelsPaymentsResponse } from 'types/channels'
import { titleCase } from 'utils/utils'
import { ChannelPaymentSidePanel } from './ChannelPaymentSidePanel'
import { TransactionStatusHighlight } from './TransactionStatusHighlight'

interface ChannelPaymentTableProps {
  channelPayments: ChannelsPaymentsResponse
}

const Table = ({ channelPayments }: ChannelPaymentTableProps) => {
  const { t } = useTranslation()

  const {
    isSidePanelOpen: isChannelPaymentDetailsOpen,
    sidePanelData: channelPaymentDetails,
    paging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(channelPayments)

  const tableColumns: DataTableHeaderProps[] = [
    {
      key: 'channel-reference',
      label: t('payments.channels.channelReference'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'amount',
      label: t('payments.amount'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'merchantId',
      label: t('payments.channels.merchantId'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'date',
      label: t('payments.date'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'status',
      label: t('payments.status'),
      variant: DataTableCellVariants.TEXT,
    },
  ]

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!channelPayments?.length) return []

    return channelPayments?.map((transaction, index) => {
      return {
        id: `${transaction?.reference}-${transaction?.hash}-${transaction?.dateCreated}`,
        testId: `${transaction?.reference}-${transaction?.hash}-${transaction?.dateCreated}`,
        onRowClick: () => {
          setIsSidePanelOpen(true)
          updateSidePanel(index)
        },
        cells: [
          {
            key: 'channel-reference',
            variant: DataTableCellVariants.TEXT,
            Text: transaction?.reference,
          },
          {
            key: 'amount',
            variant: DataTableCellVariants.TEXT,
            Text: `${transaction?.paidAmount} ${transaction?.paidCurrency}`,
          },
          {
            key: 'merchantId',
            variant: DataTableCellVariants.TEXT,
            Text: transaction?.merchantId,
          },
          {
            key: 'date',
            variant: DataTableCellVariants.TEXT,
            Text: `${dayjs(transaction?.dateCreated).format('DD MMM YYYY')}`,
          },
          {
            key: 'status',
            variant: DataTableCellVariants.HIGHLIGHT,
            Text: titleCase(transaction?.status),
            Highlights: [
              <TransactionStatusHighlight
                key={`${transaction?.status}-${transaction?.reference}`}
                status={transaction?.status}
              />,
            ],
          },
        ],
      }
    })
  }, [channelPayments])

  return (
    <Box paddingT={20}>
      <DataTable headers={tableColumns} data={tableData} />
      <ChannelPaymentSidePanel
        isOpen={isChannelPaymentDetailsOpen}
        paging={paging}
        onClose={() => setIsSidePanelOpen(false)}
        channelPaymentDetails={channelPaymentDetails}
      />
    </Box>
  )
}

export default React.memo(Table)
